import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { Button } from '@/components/ui/Button';
import { nav } from '@/content/site';
import logoUrl from '@/assets/logo-full.png';
import styles from './Header.module.css';

/**
 * Responsive strategy (deliberate, unlike the source site which used one
 * off-canvas menu at every breakpoint):
 *  - >= 1024px (desktop): real inline horizontal nav, dropdowns open on
 *    hover/focus.
 *  - < 1024px (tablet + mobile): hamburger trigger opens a full-screen
 *    off-canvas panel with an accordion-style expand for Services/Products.
 */
export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMobileOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  return (
    <header className={styles.header}>
      <div className={styles.bar}>
        <NavLink to="/" className={styles.logoLink} aria-label="AniMon Infotech Solutions, home">
          <img src={logoUrl} alt="" className={styles.logo} width={2172} height={724} />
        </NavLink>

        <nav className={styles.desktopNav} aria-label="Primary">
          <ul className={styles.desktopList}>
            {nav.map((item) => (
              <li key={item.href} className={styles.desktopItem}>
                {item.children ? (
                  <>
                    <NavLink
                      to={item.href}
                      className={({ isActive }) =>
                        isActive
                          ? `${styles.desktopTrigger} ${styles.active}`
                          : styles.desktopTrigger
                      }
                      aria-haspopup="true"
                      // The dropdown is pure CSS (:hover/:focus-within) — in an
                      // SPA, focus left on this link after a click has no
                      // reason to move on its own, so :focus-within stays true
                      // and the panel stays open even after the mouse leaves.
                      // Dropping focus on click lets :hover alone decide once
                      // the pointer moves away.
                      onClick={(e) => e.currentTarget.blur()}
                    >
                      {item.label}
                      <Icon name="chevronDown" size={14} />
                    </NavLink>
                    <ul className={styles.dropdown}>
                      {item.children.map((child) => (
                        <li key={child.href}>
                          <NavLink
                            to={child.href}
                            className={styles.dropdownLink}
                            onClick={(e) => e.currentTarget.blur()}
                          >
                            {child.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <NavLink
                    to={item.href}
                    end={item.href === '/'}
                    className={({ isActive }) =>
                      isActive ? `${styles.desktopLink} ${styles.active}` : styles.desktopLink
                    }
                  >
                    {item.label}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.desktopCta}>
          <Button href="/contact" size="md">
            Get in touch
          </Button>
        </div>

        <button
          type="button"
          className={styles.menuButton}
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav-panel"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMobileOpen((v) => !v)}
        >
          <Icon name={mobileOpen ? 'close' : 'menu'} size={24} />
        </button>
      </div>

      <div
        id="mobile-nav-panel"
        ref={panelRef}
        className={styles.mobilePanel}
        data-open={mobileOpen}
        inert={!mobileOpen ? true : undefined}
      >
        <nav aria-label="Primary, mobile">
          <ul className={styles.mobileList}>
            {nav.map((item) => (
              <li key={item.href} className={styles.mobileItem}>
                {item.children ? (
                  <>
                    <button
                      type="button"
                      className={styles.mobileTrigger}
                      aria-expanded={mobileExpanded === item.label}
                      onClick={() =>
                        setMobileExpanded((cur) => (cur === item.label ? null : item.label))
                      }
                    >
                      {item.label}
                      <Icon
                        name="chevronDown"
                        size={18}
                        className={mobileExpanded === item.label ? styles.rotated : ''}
                      />
                    </button>
                    {mobileExpanded === item.label ? (
                      <ul className={styles.mobileSublist}>
                        {item.children.map((child) => (
                          <li key={child.href}>
                            <NavLink
                              to={child.href}
                              className={styles.mobileSublink}
                              onClick={() => setMobileOpen(false)}
                            >
                              {child.label}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </>
                ) : (
                  <NavLink
                    to={item.href}
                    end={item.href === '/'}
                    className={styles.mobileLink}
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
          <Button href="/contact" size="lg" className={styles.mobileCta}>
            Get in touch
          </Button>
        </nav>
      </div>
    </header>
  );
}
